import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Heart,
  MapPin,
  Phone,
  User,
  Users,
  Activity,
  ShieldAlert
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { SINGAPORE_DISTRICTS } from '../constants/singaporeRegions';

const ElderlyOrderSubmissionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const hasShownPrefilledToast = useRef(false);

  const [formData, setFormData] = useState({
    // Service selection
    serviceType: '',
    serviceName: '',

    // Elder Information
    elderInfo: {
      firstName: '',
      lastName: '',
      age: '',
      gender: '',
      relationship: '', // Self, Parent, Grandparent, Relative, Friend
      livingSituation: '', // Alone, With Family, Assisted Living
    },

    // Health & Care Needs
    careNeeds: {
      mobilityLevel: '', // Independent, Needs Assistance, Wheelchair, Bedridden
      medicalConditions: [],
      medications: '',
      allergies: '',
      specialNeeds: '',
      cognitiveStatus: '', // Alert, Mild Impairment, Moderate, Severe
    },

    // Service Address
    location: {
      streetAddress: '',
      building: '',
      unit: '',
      city: 'Central Singapore',
      state: 'Singapore',
      zipCode: '',
      country: 'Singapore'
    },

    // Schedule Preferences
    schedule: {
      preferredDays: [], // Weekdays, Weekends, Specific days
      preferredTime: '', // Morning, Afternoon, Evening
      frequency: '', // Daily, Weekly, Bi-weekly, Monthly, One-time
      startDate: '',
      duration: '', // Hours per visit
    },

    // Emergency Contacts
    emergencyContacts: [
      {
        name: '',
        relationship: '',
        phone: '',
        isPrimary: true
      }
    ],

    // Additional Information
    customerContactNumber: user?.phone || '',
    specialInstructions: '',
    accessInstructions: ''
  });

  // Service-specific configurations
  const serviceConfigs = {
    '1': { // Basic Health Checks
      name: 'Basic Health Checks',
      suggestedFrequency: 'Weekly',
      suggestedDuration: '1 hour',
      suggestedTime: 'Morning',
      description: 'Regular health monitoring and vital signs checks'
    },
    '2': { // Preventive Home Maintenance
      name: 'Preventive Home Maintenance',
      suggestedFrequency: 'Monthly',
      suggestedDuration: '2 hours',
      suggestedTime: 'Morning',
      description: 'Safety inspection and home maintenance'
    },
    '3': { // Daily Check-In Call
      name: 'Daily Check-In Call',
      suggestedFrequency: 'Daily',
      suggestedDuration: '1 hour',
      suggestedTime: 'Morning',
      description: 'Daily phone or WhatsApp wellness check'
    },
    '4': { // Grocery Restock
      name: 'Grocery Restock',
      suggestedFrequency: 'Weekly',
      suggestedDuration: '2 hours',
      suggestedTime: 'Morning',
      description: 'Grocery delivery and essentials restock'
    },
    '5': { // Housekeeping
      name: 'Housekeeping',
      suggestedFrequency: 'Weekly',
      suggestedDuration: '3 hours',
      suggestedTime: 'Morning',
      description: 'Cleaning, laundry, and home organization'
    },
    '6': { // 24-Hour AI Camera
      name: '24-Hour AI Camera',
      suggestedFrequency: 'One-time',
      suggestedDuration: '24 hours',
      suggestedTime: 'Morning',
      description: 'AI-powered safety monitoring system installation'
    },
    '7': { // Fitbit Emergency Response
      name: 'Fitbit Emergency Response',
      suggestedFrequency: 'One-time',
      suggestedDuration: '1 hour',
      suggestedTime: 'Morning',
      description: 'Wearable emergency response device setup'
    }
  };

  // Handle pre-filled data from service detail page
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const serviceId = searchParams.get('service');
    const serviceTitle = searchParams.get('title');
    const serviceCategory = searchParams.get('category');

    if (serviceTitle && serviceCategory === 'elderly-care' && !hasShownPrefilledToast.current) {
      const config = serviceConfigs[serviceId];

      setFormData(prev => ({
        ...prev,
        serviceType: serviceId,
        serviceName: config?.name || decodeURIComponent(serviceTitle),
        // Pre-fill schedule based on service type
        schedule: {
          ...prev.schedule,
          frequency: config?.suggestedFrequency || prev.schedule.frequency,
          duration: config?.suggestedDuration || prev.schedule.duration,
          preferredTime: config?.suggestedTime || prev.schedule.preferredTime
        }
      }));

      toast.success(`✓ Selected: ${config?.name || decodeURIComponent(serviceTitle)}`);
      hasShownPrefilledToast.current = true;
    }

    // Auto-fill address from user profile
    if (user && user.address) {
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          streetAddress: user.address || prev.location.streetAddress,
          city: user.city || prev.location.city,
          zipCode: user.zipCode || prev.location.zipCode,
        }
      }));
    }
  }, [location.search, user]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayChange = (field, value, isChecked) => {
    setFormData(prev => {
      const current = prev.careNeeds[field] || [];
      if (isChecked) {
        return {
          ...prev,
          careNeeds: {
            ...prev.careNeeds,
            [field]: [...current, value]
          }
        };
      } else {
        return {
          ...prev,
          careNeeds: {
            ...prev.careNeeds,
            [field]: current.filter(item => item !== value)
          }
        };
      }
    });
  };

  const handleEmergencyContactChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  const addEmergencyContact = () => {
    setFormData(prev => ({
      ...prev,
      emergencyContacts: [
        ...prev.emergencyContacts,
        { name: '', relationship: '', phone: '', isPrimary: false }
      ]
    }));
  };

  const removeEmergencyContact = (index) => {
    if (formData.emergencyContacts.length > 1) {
      setFormData(prev => ({
        ...prev,
        emergencyContacts: prev.emergencyContacts.filter((_, i) => i !== index)
      }));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.elderInfo.firstName && formData.elderInfo.lastName && formData.elderInfo.age;
      case 2:
        return formData.careNeeds.mobilityLevel;
      case 3:
        return formData.location.streetAddress && formData.location.city && formData.location.zipCode;
      case 4:
        return formData.schedule.frequency && formData.schedule.startDate;
      case 5:
        return formData.emergencyContacts[0].name && formData.emergencyContacts[0].phone;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please login to submit a request');
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      const jobNumber = `ELDERLY${Date.now()}${Math.random().toString(36).substr(2, 4).toUpperCase()}`;

      const completeAddress = [
        formData.location.unit && `Unit ${formData.location.unit}`,
        formData.location.building,
        formData.location.streetAddress,
        formData.location.zipCode && formData.location.city && `${formData.location.zipCode} ${formData.location.city}`,
        formData.location.state
      ].filter(Boolean).join(', ');

      const orderData = {
        customerId: user.id || user._id,
        jobNumber: jobNumber,
        title: `${formData.serviceName} for ${formData.elderInfo.firstName} ${formData.elderInfo.lastName}`,
        description: `Elderly care service: ${formData.serviceName}`,
        category: 'elderly-care',
        status: 'PENDING',
        serviceCategory: 'elderly-care',

        // Elderly specific data
        elderlyCarePlan: {
          elderInfo: formData.elderInfo,
          careNeeds: formData.careNeeds,
          schedule: formData.schedule,
          emergencyContacts: formData.emergencyContacts
        },

        location: {
          ...formData.location,
          address: completeAddress || formData.location.streetAddress
        },

        requestedTimeSlot: {
          date: formData.schedule.startDate,
          startTime: '09:00',
          endTime: '17:00'
        },

        customerContactNumber: formData.customerContactNumber,
        specialInstructions: formData.specialInstructions,
        accessInstructions: formData.accessInstructions,

        items: [{
          serviceName: formData.serviceName,
          category: 'elderly-care',
          description: `Care service for ${formData.elderInfo.firstName}`,
          quantity: 1,
          unitPrice: 0,
          totalPrice: 0
        }],

        subtotal: 0,
        totalAmount: 0
      };

      const response = await api.jobs.create(orderData);

      toast.success('Care request submitted successfully! 💙');
      toast.success('Our care coordinator will contact you within 2 hours.');

      navigate('/dashboard', {
        state: {
          message: 'Elderly care request submitted successfully!',
          orderId: response.job?._id || response.job?.id
        }
      });

    } catch (error) {
      console.error('Order submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit care request');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Elder Information', icon: User },
    { number: 2, title: 'Care Needs', icon: Activity },
    { number: 3, title: 'Service Location', icon: MapPin },
    { number: 4, title: 'Schedule', icon: Calendar },
    { number: 5, title: 'Emergency Contacts', icon: Phone },
    { number: 6, title: 'Review & Submit', icon: CheckCircle }
  ];

  const medicalConditions = [
    'Diabetes',
    'Hypertension',
    'Heart Disease',
    'Arthritis',
    'Dementia/Alzheimer\'s',
    'Parkinson\'s',
    'Stroke Recovery',
    'Cancer',
    'Kidney Disease',
    'Respiratory Issues',
    'Vision Impairment',
    'Hearing Impairment'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner Header */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-white bg-opacity-20 rounded-xl mr-4">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-4xl font-bold mb-2">Elderly Care Request</h1>
                <p className="text-orange-100 text-xl">Professional care and support for your loved ones</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <div className="bg-white bg-opacity-10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-sm text-orange-100">Care Support</div>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">Experienced</div>
                <div className="text-sm text-orange-100">Care Professionals</div>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">100%</div>
                <div className="text-sm text-orange-100">Personalized Care</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selected Service Display */}
        {formData.serviceName && (
          <div className="mb-6 bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Heart className="w-5 h-5 text-orange-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Selected Service</div>
                  <div className="text-lg font-semibold text-gray-900">{formData.serviceName}</div>
                  {serviceConfigs[formData.serviceType]?.description && (
                    <div className="text-sm text-gray-500 mt-1">
                      {serviceConfigs[formData.serviceType].description}
                    </div>
                  )}
                </div>
              </div>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <div key={step.number} className="flex flex-col items-center flex-1">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isCompleted ? 'bg-green-500 border-green-500 text-white' :
                    isActive ? 'bg-orange-500 border-orange-500 text-white' :
                    'bg-white border-gray-300 text-gray-400'
                  }`}>
                    <Icon size={20} />
                  </div>
                  <span className={`mt-2 text-xs ${
                    isActive ? 'text-orange-600 font-medium' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-6"
        >
          {/* Step 1: Elder Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                <User className="inline w-6 h-6 mr-2 text-orange-600" />
                Who needs care?
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.elderInfo.firstName}
                    onChange={(e) => handleInputChange('elderInfo.firstName', e.target.value)}
                    placeholder="Elder's first name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.elderInfo.lastName}
                    onChange={(e) => handleInputChange('elderInfo.lastName', e.target.value)}
                    placeholder="Elder's last name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age *
                  </label>
                  <input
                    type="number"
                    value={formData.elderInfo.age}
                    onChange={(e) => handleInputChange('elderInfo.age', e.target.value)}
                    placeholder="Age"
                    min="50"
                    max="120"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender
                  </label>
                  <select
                    value={formData.elderInfo.gender}
                    onChange={(e) => handleInputChange('elderInfo.gender', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Prefer not to say</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Relationship to Elder
                </label>
                <select
                  value={formData.elderInfo.relationship}
                  onChange={(e) => handleInputChange('elderInfo.relationship', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select relationship</option>
                  <option value="Self">Self (I need care)</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Parent">Parent</option>
                  <option value="Grandparent">Grandparent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Relative">Other Relative</option>
                  <option value="Friend">Friend</option>
                  <option value="Caregiver">Professional Caregiver</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Living Situation
                </label>
                <select
                  value={formData.elderInfo.livingSituation}
                  onChange={(e) => handleInputChange('elderInfo.livingSituation', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select living situation</option>
                  <option value="Alone">Lives alone</option>
                  <option value="With Family">Lives with family</option>
                  <option value="With Spouse">Lives with spouse</option>
                  <option value="Assisted Living">Assisted living facility</option>
                  <option value="Nursing Home">Nursing home</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Care Needs */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                <Activity className="inline w-6 h-6 mr-2 text-orange-600" />
                What kind of care is needed?
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobility Level *
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'Independent', label: 'Fully independent - can move around without assistance' },
                    { value: 'Needs Assistance', label: 'Needs some assistance - uses walker or cane' },
                    { value: 'Wheelchair', label: 'Uses wheelchair - needs help with transfers' },
                    { value: 'Bedridden', label: 'Bedridden - requires full assistance' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="mobilityLevel"
                        value={option.value}
                        checked={formData.careNeeds.mobilityLevel === option.value}
                        onChange={(e) => handleInputChange('careNeeds.mobilityLevel', e.target.value)}
                        className="mt-1 text-orange-600"
                      />
                      <div className="ml-3">
                        <div className="font-medium text-gray-900">{option.value}</div>
                        <div className="text-sm text-gray-600">{option.label}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medical Conditions (Select all that apply)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {medicalConditions.map((condition) => (
                    <label key={condition} className="flex items-center p-2 border rounded hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={(formData.careNeeds.medicalConditions || []).includes(condition)}
                        onChange={(e) => handleArrayChange('medicalConditions', condition, e.target.checked)}
                        className="text-orange-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{condition}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Medications
                </label>
                <textarea
                  value={formData.careNeeds.medications}
                  onChange={(e) => handleInputChange('careNeeds.medications', e.target.value)}
                  rows={3}
                  placeholder="List medications, dosages, and frequency..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allergies
                </label>
                <input
                  type="text"
                  value={formData.careNeeds.allergies}
                  onChange={(e) => handleInputChange('careNeeds.allergies', e.target.value)}
                  placeholder="Food allergies, medication allergies, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cognitive Status
                </label>
                <select
                  value={formData.careNeeds.cognitiveStatus}
                  onChange={(e) => handleInputChange('careNeeds.cognitiveStatus', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select cognitive status</option>
                  <option value="Alert">Alert and oriented</option>
                  <option value="Mild">Mild cognitive impairment</option>
                  <option value="Moderate">Moderate cognitive impairment</option>
                  <option value="Severe">Severe cognitive impairment/Dementia</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Care Needs or Instructions
                </label>
                <textarea
                  value={formData.careNeeds.specialNeeds}
                  onChange={(e) => handleInputChange('careNeeds.specialNeeds', e.target.value)}
                  rows={4}
                  placeholder="Any special dietary needs, behavioral concerns, communication preferences, or other important information..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          )}

          {/* Step 3: Service Location */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                <MapPin className="inline w-6 h-6 mr-2 text-orange-600" />
                Where will care be provided?
              </h2>

              {user?.address && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <div className="flex-1 text-sm text-green-800">
                    Address auto-filled from your profile. You can edit it below if needed.
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  value={formData.location.streetAddress}
                  onChange={(e) => handleInputChange('location.streetAddress', e.target.value)}
                  placeholder="e.g., 123 Orchard Road"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Building Name
                  </label>
                  <input
                    type="text"
                    value={formData.location.building}
                    onChange={(e) => handleInputChange('location.building', e.target.value)}
                    placeholder="Building or complex name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Number
                  </label>
                  <input
                    type="text"
                    value={formData.location.unit}
                    onChange={(e) => handleInputChange('location.unit', e.target.value)}
                    placeholder="e.g., #05-123"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Area/District *
                  </label>
                  <select
                    value={formData.location.city}
                    onChange={(e) => handleInputChange('location.city', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select Area</option>
                    {SINGAPORE_DISTRICTS.map((district) => (
                      <option key={district.value} value={district.value}>
                        {district.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    value={formData.location.zipCode}
                    onChange={(e) => handleInputChange('location.zipCode', e.target.value)}
                    placeholder="e.g., 123456"
                    pattern="[0-9]{6}"
                    maxLength="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Instructions
                </label>
                <textarea
                  value={formData.accessInstructions}
                  onChange={(e) => handleInputChange('accessInstructions', e.target.value)}
                  rows={3}
                  placeholder="How to access the property (gate codes, parking, elevator access, etc.)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          )}

          {/* Step 4: Schedule */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                <Calendar className="inline w-6 h-6 mr-2 text-orange-600" />
                When do you need care services?
              </h2>

              {/* Suggested Schedule Info */}
              {serviceConfigs[formData.serviceType] && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-blue-900 mb-1">
                        Recommended for {formData.serviceName}:
                      </div>
                      <ul className="text-blue-800 space-y-1">
                        <li>• Frequency: <strong>{serviceConfigs[formData.serviceType].suggestedFrequency}</strong></li>
                        <li>• Duration: <strong>{serviceConfigs[formData.serviceType].suggestedDuration}</strong></li>
                        <li>• Best Time: <strong>{serviceConfigs[formData.serviceType].suggestedTime}</strong></li>
                      </ul>
                      <div className="text-blue-700 mt-2 text-xs">
                        These are pre-selected suggestions. You can adjust them based on your needs.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Frequency *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { value: 'Daily', label: 'Daily', desc: 'Everyday care service' },
                    { value: 'Weekdays', label: 'Weekdays Only', desc: 'Monday to Friday' },
                    { value: 'Weekly', label: 'Weekly', desc: 'Once per week' },
                    { value: 'Bi-weekly', label: 'Bi-weekly', desc: 'Every two weeks' },
                    { value: 'Monthly', label: 'Monthly', desc: 'Once per month' },
                    { value: 'One-time', label: 'One-time', desc: 'Single visit' }
                  ].map((option) => {
                    const isRecommended = serviceConfigs[formData.serviceType]?.suggestedFrequency === option.value;
                    return (
                      <label key={option.value} className={`flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                        isRecommended ? 'border-blue-400 bg-blue-50' : ''
                      }`}>
                        <input
                          type="radio"
                          name="frequency"
                          value={option.value}
                          checked={formData.schedule.frequency === option.value}
                          onChange={(e) => handleInputChange('schedule.frequency', e.target.value)}
                          className="mt-1 text-orange-600"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-gray-900">{option.label}</div>
                            {isRecommended && (
                              <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{option.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time of Day
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'Morning', label: 'Morning', time: '8 AM - 12 PM' },
                    { value: 'Afternoon', label: 'Afternoon', time: '12 PM - 5 PM' },
                    { value: 'Evening', label: 'Evening', time: '5 PM - 8 PM' }
                  ].map((option) => {
                    const isRecommended = serviceConfigs[formData.serviceType]?.suggestedTime === option.value;
                    return (
                      <label key={option.value} className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                        isRecommended ? 'border-blue-400 bg-blue-50' : ''
                      }`}>
                        <input
                          type="radio"
                          name="preferredTime"
                          value={option.value}
                          checked={formData.schedule.preferredTime === option.value}
                          onChange={(e) => handleInputChange('schedule.preferredTime', e.target.value)}
                          className="text-orange-600"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-gray-900">{option.label}</div>
                            {isRecommended && (
                              <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600">{option.time}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  When to start? *
                </label>
                <input
                  type="date"
                  value={formData.schedule.startDate}
                  onChange={(e) => handleInputChange('schedule.startDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration per Visit
                </label>
                <select
                  value={formData.schedule.duration}
                  onChange={(e) => handleInputChange('schedule.duration', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select duration</option>
                  <option value="1 hour">1 hour</option>
                  <option value="2 hours">2 hours</option>
                  <option value="3 hours">3 hours</option>
                  <option value="4 hours">4 hours (Half day)</option>
                  <option value="8 hours">8 hours (Full day)</option>
                  <option value="12 hours">12 hours</option>
                  <option value="24 hours">24 hours (Live-in)</option>
                </select>
                {serviceConfigs[formData.serviceType]?.suggestedDuration && (
                  <p className="mt-2 text-sm text-blue-600">
                    💡 Recommended: <strong>{serviceConfigs[formData.serviceType].suggestedDuration}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Emergency Contacts */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                <Phone className="inline w-6 h-6 mr-2 text-orange-600" />
                Emergency Contact Information
              </h2>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <ShieldAlert className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <strong>Important:</strong> Please provide at least one emergency contact who can be reached in case of urgent situations.
                  </div>
                </div>
              </div>

              {formData.emergencyContacts.map((contact, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">
                      {contact.isPrimary ? 'Primary Contact' : `Contact ${index + 1}`}
                    </h3>
                    {!contact.isPrimary && formData.emergencyContacts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmergencyContact(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name {contact.isPrimary ? '*' : ''}
                      </label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => handleEmergencyContactChange(index, 'name', e.target.value)}
                        placeholder="Contact person name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Relationship {contact.isPrimary ? '*' : ''}
                      </label>
                      <input
                        type="text"
                        value={contact.relationship}
                        onChange={(e) => handleEmergencyContactChange(index, 'relationship', e.target.value)}
                        placeholder="e.g., Son, Daughter, Spouse"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number {contact.isPrimary ? '*' : ''}
                    </label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => handleEmergencyContactChange(index, 'phone', e.target.value)}
                      placeholder="+65 XXXX XXXX"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addEmergencyContact}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-500 hover:text-orange-600 transition-colors"
              >
                + Add Another Emergency Contact
              </button>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Contact Number
                </label>
                <input
                  type="tel"
                  value={formData.customerContactNumber}
                  onChange={(e) => handleInputChange('customerContactNumber', e.target.value)}
                  placeholder="Your phone number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="text-sm text-gray-500 mt-1">For care coordinator to contact you</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={formData.specialInstructions}
                  onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                  rows={4}
                  placeholder="Any other important information we should know..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          )}

          {/* Step 6: Review & Submit */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Review Your Care Request</h2>

              {/* Service Summary */}
              {formData.serviceName && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-900 mb-3">Selected Service</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <Heart className="w-5 h-5 text-orange-600 mr-2" />
                      <strong className="text-lg text-orange-900">{formData.serviceName}</strong>
                    </div>
                    {serviceConfigs[formData.serviceType]?.description && (
                      <div className="text-orange-800">{serviceConfigs[formData.serviceType].description}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Elder Information Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Elder Information</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Name:</strong> {formData.elderInfo.firstName} {formData.elderInfo.lastName}</div>
                  <div><strong>Age:</strong> {formData.elderInfo.age} years old</div>
                  {formData.elderInfo.gender && <div><strong>Gender:</strong> {formData.elderInfo.gender}</div>}
                  {formData.elderInfo.relationship && <div><strong>Relationship:</strong> {formData.elderInfo.relationship}</div>}
                  {formData.elderInfo.livingSituation && <div><strong>Living Situation:</strong> {formData.elderInfo.livingSituation}</div>}
                </div>
              </div>

              {/* Care Needs Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Care Requirements</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Mobility:</strong> {formData.careNeeds.mobilityLevel}</div>
                  {formData.careNeeds.medicalConditions && formData.careNeeds.medicalConditions.length > 0 && (
                    <div><strong>Medical Conditions:</strong> {formData.careNeeds.medicalConditions.join(', ')}</div>
                  )}
                  {formData.careNeeds.cognitiveStatus && (
                    <div><strong>Cognitive Status:</strong> {formData.careNeeds.cognitiveStatus}</div>
                  )}
                </div>
              </div>

              {/* Location Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Service Location</h3>
                <div className="text-sm">
                  {formData.location.unit && <div>Unit {formData.location.unit}</div>}
                  {formData.location.building && <div>{formData.location.building}</div>}
                  <div>{formData.location.streetAddress}</div>
                  <div>{formData.location.city}, Singapore {formData.location.zipCode}</div>
                </div>
              </div>

              {/* Schedule Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Service Schedule</h3>
                <div className="text-sm space-y-1">
                  <div><strong>Frequency:</strong> {formData.schedule.frequency}</div>
                  {formData.schedule.preferredTime && <div><strong>Preferred Time:</strong> {formData.schedule.preferredTime}</div>}
                  <div><strong>Start Date:</strong> {formData.schedule.startDate}</div>
                  {formData.schedule.duration && <div><strong>Duration:</strong> {formData.schedule.duration}</div>}
                </div>
              </div>

              {/* Emergency Contacts Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Emergency Contacts</h3>
                {formData.emergencyContacts.map((contact, index) => (
                  contact.name && (
                    <div key={index} className="text-sm mb-2">
                      <div><strong>{contact.isPrimary ? 'Primary Contact' : `Contact ${index + 1}`}:</strong> {contact.name}</div>
                      {contact.relationship && <div className="text-gray-600">Relationship: {contact.relationship}</div>}
                      {contact.phone && <div className="text-gray-600">Phone: {contact.phone}</div>}
                    </div>
                  )
                ))}
              </div>

              {/* What happens next */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3">What happens next?</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                  <li>Our care coordinator will review your request within 2 hours</li>
                  <li>We'll contact you to discuss the care plan and answer questions</li>
                  <li>We'll match you with qualified, experienced caregivers</li>
                  <li>You'll meet the caregiver(s) before services begin</li>
                  <li>We'll provide a detailed care plan and pricing</li>
                  <li>Once approved, care services begin as scheduled!</li>
                </ol>
              </div>
            </div>
          )}
        </motion.div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
          ) : (
            <div></div>
          )}

          {currentStep < 6 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!validateStep(currentStep)}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Submit Care Request
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ElderlyOrderSubmissionPage;
